import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Descriptions, message, Card, Spin, Space, Popconfirm,
  Modal, Form, Input, Select, Tabs, Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, SaveOutlined, DeleteOutlined, RobotOutlined,
} from '@ant-design/icons';
import { toolsApi, dictionariesApi } from '../services/api-services';
import { useStreamGeneration } from '../hooks/useStreamGeneration';
import Editor from '@monaco-editor/react';
import MdEditor from 'react-markdown-editor-lite';
import MarkdownIt from 'markdown-it';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'react-markdown-editor-lite/lib/index.css';
import '../styles/markdown.css';
import { useTheme } from '../contexts/ThemeContext';

const { TextArea } = Input;
const mdParser = new MarkdownIt();

const ToolDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { generate: streamGenerate } = useStreamGeneration();
  const [tool, setTool] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();

  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  const [isEditingCode, setIsEditingCode] = useState(false);
  const [editedCode, setEditedCode] = useState('');
  const [scriptNameInput, setScriptNameInput] = useState('');
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isFullscreen = !!document.querySelector('.rc-md-editor.full');
      document.documentElement.classList.toggle('md-fullscreen', isFullscreen);
    });
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
      document.documentElement.classList.remove('md-fullscreen');
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await toolsApi.get(Number(id));
      setTool(res);
    } catch {
      message.error('获取 Tool 详情失败');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (id) fetchData();
    dictionariesApi.getByCode('resource_tag').then((dict: any) => {
      if (dict?.items) setTagOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    }).catch(() => {});
  }, [id, fetchData]);

  const handleSaveContent = async () => {
    try {
      await toolsApi.update(Number(id), { content: editedContent });
      message.success('内容保存成功');
      setIsEditingContent(false);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '保存失败');
    }
  };

  const handleGenerate = () => {
    // 退出编辑模式
    setIsEditingContent(false);
    let collectedContent = '';
    
    streamGenerate({
      action: 'create_task',
      payload: { 
        key: 'generate_tool',
        id: Number(id),
        resourceName: `${tool?.title || '工具'} - 说明生成`,
      },
      navigationUrl: `/tools/${id}`,
      onChunk: (chunk: string) => {
        collectedContent += chunk;
      },
      onDone: async () => {
        // 提取描述内容（第2行的"描述："后面的内容）
        const descMatch = collectedContent.match(/^#[^\n]*\n描述[：:]\s*([^\n]+)/);
        const descContent = descMatch ? descMatch[1].trim() : '';
        
        // 自动保存
        try {
          await toolsApi.update(Number(id), { content: collectedContent });
          // 如果提取到描述内容，同时更新 description
          if (descContent && tool) {
            await toolsApi.update(Number(id), { description: descContent });
          }
          fetchData();
        } catch (e: any) {
          message.error(e.response?.data?.detail || '保存失败');
        }
      }
    });
  };

  const handleSaveCode = async () => {
    if (!tool.script_name && !scriptNameInput.trim()) {
      message.warning('请输入脚本文件名');
      return;
    }
    try {
      const payload: any = { code: editedCode };
      if (scriptNameInput.trim() && scriptNameInput.trim() !== tool.script_name) {
        payload.script_name = scriptNameInput.trim();
      }
      await toolsApi.update(Number(id), payload);
      message.success('脚本保存成功');
      setIsEditingCode(false);
      setScriptNameInput('');
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '保存失败');
    }
  };

  const openEditModal = () => {
    editForm.setFieldsValue({
      identifier: tool.identifier,
      title: tool.title,
      description: tool.description || '',
      call_method: tool.call_method,
      script_name: tool.script_name || '',
      tags: tool.tags || [],
    });
    setEditModalVisible(true);
  };

  const handleEditSave = async (values: any) => {
    try {
      await toolsApi.update(Number(id), values);
      message.success('更新成功');
      setEditModalVisible(false);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '更新失败');
    }
  };

  const handleDelete = async () => {
    try {
      await toolsApi.delete(Number(id));
      message.success('删除成功');
      navigate('/tools');
    } catch {
      message.error('删除失败');
    }
  };

  if (loading && !tool) {
    return (
      <div className="apple-page" style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="apple-page">
        <div className="apple-empty">
          <p className="apple-empty-title">未找到 Tool</p>
          <Button type="primary" onClick={() => navigate('/tools')}>返回列表</Button>
        </div>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'info',
      label: '基础信息',
      children: (
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="标识">{tool.identifier}</Descriptions.Item>
          <Descriptions.Item label="名称">{tool.title}</Descriptions.Item>
          <Descriptions.Item label="描述">{tool.description || '-'}</Descriptions.Item>
          <Descriptions.Item label="调用方式">
            <span className={`apple-tag apple-tag-${tool.call_method === 'python' ? 'blue' : 'green'}`}>
              {tool.call_method}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="脚本文件名">{tool.script_name || <span style={{ color: 'var(--apple-text-secondary)' }}>未设置</span>}</Descriptions.Item>
          <Descriptions.Item label="创建时间">{tool.created_at ? new Date(tool.created_at).toLocaleString() : '-'}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{tool.updated_at ? new Date(tool.updated_at).toLocaleString() : '-'}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'code',
      label: '脚本',
      children: (
        <Card
          title="脚本"
          extra={
            isEditingCode ? (
              <Space>
                <Button onClick={() => setIsEditingCode(false)}>取消</Button>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveCode}>保存</Button>
              </Space>
            ) : (
              <Button type="text" icon={<EditOutlined />} onClick={() => { setEditedCode(tool.code || ''); setScriptNameInput(tool.script_name || ''); setIsEditingCode(true); }} />
            )
          }
        >
          {tool.script_name && (
            <div style={{ marginBottom: 12, color: 'var(--apple-text-secondary)', fontSize: 13 }}>
              文件名：{tool.script_name}
            </div>
          )}
          {isEditingCode ? (
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
              <Editor
                key="edit"
                height="400px"
                language={tool.call_method === 'python' ? 'python' : 'shell'}
                theme={theme === 'glass' ? 'vs-dark' : 'light'}
                value={editedCode}
                onChange={(val) => setEditedCode(val || '')}
                options={{ minimap: { enabled: false }, fontSize: 14, scrollBeyondLastLine: false, padding: { top: 12 } }}
              />
            </div>
          ) : (
            <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
              <Editor
                key="view"
                height="400px"
                language={tool.call_method === 'python' ? 'python' : 'shell'}
                theme={theme === 'glass' ? 'vs-dark' : 'light'}
                value={tool.code || ''}
                options={{ minimap: { enabled: false }, fontSize: 14, readOnly: true, scrollBeyondLastLine: false, padding: { top: 12 } }}
              />
            </div>
          )}
        </Card>
      ),
    },
    {
      key: 'content',
      label: '内容',
      children: (
        <Card
          title="内容"
          styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: '0 0 8px 8px' } }}
          extra={
            isEditingContent ? (
              <Space>
                <Button icon={<RobotOutlined />} onClick={handleGenerate}>AI 生成</Button>
                <Button onClick={() => setIsEditingContent(false)}>取消</Button>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveContent}>保存</Button>
              </Space>
            ) : (
              <Button type="text" icon={<EditOutlined />} onClick={() => { setEditedContent(tool.content || ''); setIsEditingContent(true); }} />
            )
          }
        >
          {isEditingContent ? (
            <MdEditor
              value={editedContent}
              style={{ height: '400px' }}
              renderHTML={(text) => mdParser.render(text)}
              onChange={({ text }) => setEditedContent(text)}
              config={{
                view: { menu: true, md: true, html: true },
                canView: { menu: true, md: true, html: true, fullScreen: true, hideMenu: true },
              }}
            />
          ) : (
            <div className="markdown-preview">
              {tool.content ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{tool.content}</ReactMarkdown> : <p style={{ color: 'var(--apple-text-secondary)', padding: 24 }}>暂无内容</p>}
            </div>
          )}
        </Card>
      ),
    },
  ];

  return (
    <div className="apple-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space align="center">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/tools')} />
          <h1 className="apple-page-title" style={{ marginBottom: 0 }}>{tool.title}</h1>
        </Space>
        <Space>
          <Button icon={<EditOutlined />} onClick={openEditModal}>编辑</Button>
          <Popconfirm title="确认删除" description="确定要删除此 Tool 吗？" onConfirm={handleDelete} okText="确定" cancelText="取消">
            <Button danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      </div>

      <Tabs items={tabItems} />

      <Modal
        className="apple-modal"
        title="编辑 Tool"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => editForm.submit()}
        destroyOnHidden
      >
        <Form className="apple-form" form={editForm} layout="vertical" onFinish={handleEditSave}>
          <Form.Item name="identifier" label="标识" rules={[{ required: true }, { pattern: /^[a-z0-9-]+$/, message: '仅支持小写字母、数字和连字符' }, { max: 64, message: '最长64字符' }]}><Input /></Form.Item>
          <Form.Item name="title" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述"><TextArea rows={3} /></Form.Item>
          <Form.Item name="call_method" label="调用方式" rules={[{ required: true }]}>
            <Select options={[{ label: 'Python', value: 'python' }, { label: 'Shell', value: 'shell' }]} />
          </Form.Item>
          <Form.Item name="script_name" label="脚本文件名" rules={[{ required: true, message: '请填写脚本文件名' }]}>
            <Input placeholder="如 main.py、run.sh" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ToolDetail;
